
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight } from 'lucide-react';

const Blog = () => {
  const { t } = useLanguage();

  const blogPosts = [
    {
      title: "The Future of Mental Health: Global Access Through Technology",
      excerpt: "Explore how digital platforms are revolutionizing mental health care access worldwide and breaking down geographical barriers.",
      author: "Dr. José Luis Marín",
      date: "2024-01-15",
      readTime: "8 min read",
      category: "Mental Health"
    },
    {
      title: "Understanding Brief Psychotherapy: An Evidence-Based Approach",
      excerpt: "Learn about the principles and effectiveness of brief psychotherapy techniques in modern mental health treatment.",
      author: "Dr. José Luis Marín",
      date: "2024-01-10",
      readTime: "12 min read",
      category: "Therapy Techniques"
    },
    {
      title: "Building Therapeutic Relationships in Digital Spaces",
      excerpt: "Discover strategies for creating meaningful therapeutic connections through online therapy platforms.",
      author: "Clinical Team",
      date: "2024-01-05",
      readTime: "6 min read",
      category: "Online Therapy"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Mental Health Blog & Resources | Global Therapy Hub</title>
        <meta name="description" content="Read insights, research, and expert advice on mental health, therapy techniques, and professional development from our team of experts." />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-green-50 to-blue-100">
          <div className="container mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('mentalHealthBlog')}
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              {t('blogDescription')}
            </p>
          </div>
        </section>

        {/* Blog Posts */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-8">
              {blogPosts.map((post, index) => (
                <article key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="p-6">
                    <div className="mb-4">
                      <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                        {post.category}
                      </span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-1" />
                          {post.author}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {post.date}
                        </div>
                      </div>
                      <span>{post.readTime}</span>
                    </div>
                    <Button variant="outline" className="flex items-center" disabled>
                      Read More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Coming Soon */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">More Content Coming Soon</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              We're working on bringing you more valuable content about mental health, 
              therapy techniques, and professional development. Stay tuned for regular updates!
            </p>
            <Button variant="outline" disabled>
              Subscribe to Newsletter
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default Blog;
